import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MDLINK_LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function Register() {
    const { user, register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) {
            toast.error("La contraseña debe tener al menos 6 caracteres");
            return;
        }
        setLoading(true);
        const res = await register(form.name, form.email, form.password);
        setLoading(false);
        if (res.ok) {
            toast.success("Cuenta creada. ¡Bienvenido!");
            navigate("/");
        } else {
            toast.error(res.error || "No se pudo crear la cuenta");
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] bg-white">
            <div className="flex items-center justify-center px-6 py-10 lg:px-14">
                <div className="w-full max-w-md">
                    <Link to="/login" className="flex items-center gap-4 mb-10">
                        <img
                            src={MDLINK_LOGO}
                            alt="MD Link"
                            className="h-12 w-auto"
                            draggable={false}
                        />
                        <div className="pl-4 border-l border-slate-200 leading-tight">
                            <div className="font-heading font-semibold text-[15px] tracking-tight text-slate-900">
                                Cotizador
                            </div>
                            <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                                Plataforma B2B · CR
                            </div>
                        </div>
                    </Link>

                    <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                        Crear cuenta.
                    </h1>
                    <p className="mt-3 text-slate-600 leading-relaxed">
                        Registrate para empezar a generar cotizaciones profesionales.
                    </p>

                    <form className="mt-8 space-y-5" onSubmit={onSubmit} data-testid="register-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="reg-name" className="text-xs font-mono uppercase tracking-widest text-slate-500">
                                Nombre completo
                            </Label>
                            <Input
                                id="reg-name"
                                data-testid="register-name-input"
                                required
                                value={form.name}
                                onChange={set("name")}
                                placeholder="Tu nombre"
                                className="h-11 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9]"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="reg-email" className="text-xs font-mono uppercase tracking-widest text-slate-500">
                                Correo
                            </Label>
                            <Input
                                id="reg-email"
                                data-testid="register-email-input"
                                type="email"
                                autoComplete="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                placeholder="tu@empresa.com"
                                className="h-11 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9]"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="reg-password" className="text-xs font-mono uppercase tracking-widest text-slate-500">
                                Contraseña
                            </Label>
                            <div className="relative">
                                <Input
                                    id="reg-password"
                                    data-testid="register-password-input"
                                    type={showPwd ? "text" : "password"}
                                    required
                                    minLength={6}
                                    value={form.password}
                                    onChange={set("password")}
                                    placeholder="Mínimo 6 caracteres"
                                    className="h-11 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9] pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    className="absolute inset-y-0 right-2 grid place-items-center text-slate-400 hover:text-slate-700"
                                    aria-label="Mostrar contraseña"
                                >
                                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            data-testid="register-submit-button"
                            type="submit"
                            disabled={loading}
                            className="h-11 w-full rounded-md bg-[#1BA5D9] hover:bg-[#0E7DAB] active:scale-[.99] transition-all duration-150"
                        >
                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Crear cuenta
                        </Button>

                        <div className="text-sm text-slate-600 text-center">
                            ¿Ya tenés cuenta?{" "}
                            <Link data-testid="register-goto-login" to="/login" className="text-[#1BA5D9] font-medium hover:underline">
                                Iniciar sesión
                            </Link>
                        </div>
                    </form>
                </div>
            </div>

            <div className="hidden lg:block relative">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-[#1BA5D9]/50 to-slate-900/70" />
                <div className="absolute inset-0 bg-grid opacity-10 mix-blend-overlay" />
                <div className="relative h-full flex flex-col justify-end p-14 text-white">
                    <div className="max-w-md">
                        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/70 mb-4">
                            Diseñado para hospitales, cadenas y clínicas
                        </div>
                        <h2 className="font-heading text-3xl xl:text-4xl font-semibold tracking-tight leading-tight">
                            Un cotizador. Múltiples modelos comerciales. Cero errores.
                        </h2>
                    </div>
                </div>
            </div>
        </div>
    );
}
